/**
 * The composer's size limit, in one place.
 *
 * WHY BYTES AND NOT CHARACTERS. The server counts `byte_size(body)` against
 * `@max_body_bytes` (16 000) in both `chat_controller.ex` and
 * `chat_channel.ex`. Chat is UTF-8 end to end — Postgres, Elixir binaries, JSON
 * transport — and in UTF-8 a character is 1–4 bytes. So a character-based limit
 * is only equal to the server's for ASCII:
 *
 *   10 000 characters of English  ≈ 10 000 bytes  → sends
 *   10 000 characters of Russian  ≈ 18 056 bytes  → REJECTED
 *    6 000 characters of Japanese ≈ 18 000 bytes  → REJECTED
 *
 * That asymmetry is why the limit reads to a non-Latin writer as "chat does not
 * support my language". It always did support it; it was measuring wrong.
 *
 * WHY THE DEFAULT SITS BELOW THE SERVER'S CAP. A body near 16 000 can still be
 * pushed over by a reply quote or metadata added after this check. Converting
 * early is recoverable; a rejected send is not.
 */

/** The server's hard limit. Mirrored by hand — change one, change both. */
export const SERVER_MAX_BODY_BYTES = 16000;

/** Default client threshold, in KB, as shown in settings. */
export const DEFAULT_LONG_MESSAGE_KB = 12;

/** Bounds for the user-set threshold, in KB. */
export const MIN_LONG_MESSAGE_KB = 1;
/**
 * 15, not 16: the ceiling must stay strictly under the server's cap, or the
 * setting would let a user configure their way back into the failure this
 * feature exists to remove.
 */
export const MAX_LONG_MESSAGE_KB = 15;

/** A user-supplied KB value → a usable one. Anything unreadable → the default. */
export function clampLongMessageKb(value) {
  const n = typeof value === 'number' ? value : parseInt(value, 10);
  if (!Number.isFinite(n)) return DEFAULT_LONG_MESSAGE_KB;
  return Math.min(MAX_LONG_MESSAGE_KB, Math.max(MIN_LONG_MESSAGE_KB, Math.round(n)));
}

/** The threshold the composer actually compares against, in bytes. */
export function longMessageBytes(kb) {
  return clampLongMessageKb(kb) * 1000;
}
